<template>
    <!-- Dialog component -->
    <VJoyDialog
        :open="isDesktopScreen"
        data-testid="end-project-overlay"
        @dialog:confirm="confirmEndOfProject"
        @dialog:hide="emit('close:dialog', 'end')"
    >
        <template #dialog-header>
            {{ t('projectclient.projectview.main.actions.end.project.title', {0: projectInformation.freelancer.firstName}) }}
        </template>
        <template #dialog-body>
            <OverlayContent
                v-model:admin-comment="adminComment"
                :form-id="formId"
                :error="r$.adminComment.$errors[0]"
                :all-lots-completed="allLotsCompleted"
            />
        </template>
        <template #dialog-cancel>
            <VJoyButton variant="secondary" size="small" @click="emit('close:dialog', 'end')">
                {{ t('generic.form.cancel.label') }}
            </VJoyButton>
        </template>
        <template #dialog-confirm>
            <VJoyButton variant="main" size="small" data-testid="end-project-confirm-button" :loading="endingProject" @click="confirmEndOfProject">
                {{ t('generic.form.confirm.label') }}
            </VJoyButton>
        </template>
    </VJoyDialog>
    <!-- Bottomsheet component -->
    <VJoyBottomSheet :open="!isDesktopScreen" data-testid="end-project-overlay" @bottom-sheet:close="emit('close:dialog', 'end')">
        <template #bottom-sheet-content>
            <h3 class="joy-font-primary-650">
                {{ t('projectclient.projectview.main.actions.end.project.title', {0: projectInformation.freelancer.firstName}) }}
            </h3>
            <OverlayContent v-model:admin-comment="adminComment" :form-id="formId" :error="r$.adminComment.$errors[0]" />
        </template>
        <template #bottom-sheet-cta>
            <div class="bottom-sheet__footer">
                <VJoyButton variant="secondary" size="small" @click="emit('close:dialog', 'end')">
                    {{ t('generic.form.cancel.label') }}
                </VJoyButton>
                <VJoySubmit :action="confirmEndOfProject" data-testid="end-project-confirm-button">
                    {{ t('generic.form.confirm.label') }}
                </VJoySubmit>
            </div>
        </template>
    </VJoyBottomSheet>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {useScreenSizeStore} from '@common/stores';
    import {requiredIf} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {VJoySubmit} from '#components';
    import {useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {VJoyBottomSheet, VJoyButton, VJoyDialog} from '@maltjoy/core-vue';
    import OverlayContent from './OverlayContent.vue';
    import {useProjectInformationStore, useUserStore} from '@/common/stores';

    const {t} = useTranslation();
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    const emit = defineEmits<{
        'close:dialog': [value: 'end'];
    }>();

    defineProps<{
        allLotsCompleted?: boolean;
    }>();

    // screen size  + project info stores
    const screenSizeStore = useScreenSizeStore();
    const {isDesktopScreen} = storeToRefs(screenSizeStore);

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, endingProject} = storeToRefs(projectInformationStore);

    // handle admin validation + clientOpsModal
    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToEndProject = computed(() => roles.value.includes('ROLE_MISSION_VALIDATION'));

    const adminComment = ref('');

    const formId = 'end-project-form';
    const {r$} = useRegle(
        {adminComment},
        {
            adminComment: {
                required: requiredIf(() => !isParticipant.value && hasAuthorityToEndProject.value),
            },
        },
    );

    async function confirmEndOfProject() {
        const {valid} = await validateForm(r$, {formId});
        if (!valid) return;

        await projectInformationStore.endProject({
            projectId: projectInformation.value.projectId,
            completeTimeAndMaterialProjectRequest: {
                comment: adminComment.value,
            },
        });
        emit('close:dialog', 'end');
    }
</script>

<style lang="scss" scoped>
    .bottom-sheet {
        &__footer {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-3);
        }
    }
</style>
